import React, { useRef } from 'react';
import Popover from './Popover';

/**
 * DropdownMenu — a menu of actions anchored to a trigger.
 *
 * Props:
 *   trigger: ReactNode
 *   items: Array<Item>  where Item is one of:
 *     { id, label, icon?, onSelect?, danger?, disabled? }   — action
 *     { separator: true }                                    — divider
 *     { header: string }                                     — group label
 *   placement, align, matchTriggerWidth: forwarded to Popover
 */
const MenuList = ({ items, close }) => {
  const listRef = useRef(null);

  const focusable = () => Array.from(
    listRef.current?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])') || [],
  );

  const onKeyDown = (e) => {
    const nodes = focusable();
    if (nodes.length === 0) return;
    const idx = nodes.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nodes[(idx + 1) % nodes.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nodes[(idx - 1 + nodes.length) % nodes.length].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      nodes[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      nodes[nodes.length - 1].focus();
    }
  };

  return (
    <div className="ps-menu" ref={listRef} onKeyDown={onKeyDown}>
      {items.map((item, i) => {
        if (item.separator) return <div key={`sep-${i}`} className="ps-menu__separator" role="separator" />;
        if (item.header) return <div key={`hdr-${i}`} className="ps-menu__header">{item.header}</div>;
        return (
          <button
            key={item.id ?? i}
            type="button"
            role="menuitem"
            className={`ps-menu__item ${item.danger ? 'ps-menu__item--danger' : ''}`.trim()}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            tabIndex={-1}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={i === 0}
            onClick={() => {
              if (item.disabled) return;
              item.onSelect?.();
              close?.();
            }}
          >
            {item.icon && <span className="ps-menu__icon">{item.icon}</span>}
            <span className="ps-menu__label">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const DropdownMenu = ({
  trigger,
  items = [],
  placement = 'bottom-start',
  matchTriggerWidth = false,
  className = '',
}) => (
  <Popover
    trigger={trigger}
    placement={placement}
    role="menu"
    matchTriggerWidth={matchTriggerWidth}
    className={className}
    panelClassName="ps-popover__panel--menu"
  >
    {({ close }) => <MenuList items={items} close={close} />}
  </Popover>
);

export default DropdownMenu;
